import React, { useState } from 'react';
import { Eye, Calendar, Users, Megaphone, Wine, CheckCircle, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { submitExhibitorEnquiry } from '../api/client';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  Eye,
  Calendar,
  Users,
  Megaphone,
  Wine
};

// Category keys for i18n translation
const categoryKeys = [
  'foodBeverage',
  'cosmetics',
  'pharma',
  'fashion',
  'finance',
  'tourism',
  'technology',
  'logistics',
  'education',
  'media',
  'agriculture',
  'manufacturing',
  'retail',
  'certification',
  'government',
  'investment',
  'others'
];

export const ExhibitorSection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue, language } = useLanguage();
  const primaryColor = settings.colors?.primary || '#0E7A5F';

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    country: '',
    email: '',
    whatsapp: '',
    category: '',
    interests: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const interestOptions = [
    { id: 'delegate', labelKey: 'form.delegate', icon: Users },
    { id: 'booth', labelKey: 'form.booth', icon: Eye },
    { id: 'sponsorship', labelKey: 'form.sponsorship', icon: Megaphone }
  ];

  // Exhibitor benefits with i18n
  const exhibitorBenefits = [
    {
      icon: 'Eye',
      titleKey: 'exhibitor.globalVisibility',
      descKey: 'exhibitor.globalVisibilityDesc'
    },
    {
      icon: 'Calendar',
      titleKey: 'exhibitor.curatedMeetings',
      descKey: 'exhibitor.curatedMeetingsDesc'
    },
    {
      icon: 'Users',
      titleKey: 'exhibitor.accessDelegations',
      descKey: 'exhibitor.accessDelegationsDesc'
    },
    {
      icon: 'Megaphone',
      titleKey: 'exhibitor.marketingInclusion',
      descKey: 'exhibitor.marketingInclusionDesc'
    },
    {
      icon: 'Wine',
      titleKey: 'exhibitor.networkingEvents',
      descKey: 'exhibitor.networkingEventsDesc'
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value });
  };

  const handleInterestToggle = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(i => i !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.interests.length === 0) {
      toast.error(t('form.selectInterest'));
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        ...formData,
        interests: formData.interests.join(', ')
      };
      
      await submitExhibitorEnquiry(submissionData);
      toast.success(t('form.successMessage'));
      setFormData({
        name: '',
        company: '',
        country: '',
        email: '',
        whatsapp: '',
        category: '',
        interests: [],
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('form.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="exhibitor" className="container mx-auto px-6 py-20">
      <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">
        {getBilingualValue(settings.exhibitorTitle, 'exhibitor.title')}
      </h2>
      <div className="flex flex-col lg:flex-row gap-16">
        <div className="lg:w-1/2">
          <h3 className="text-2xl font-bold mb-8">
            {getBilingualValue(settings.exhibitorBenefitsTitle, 'exhibitor.benefitsTitle')}
          </h3>
          <ul className="space-y-6 mb-12">
            {exhibitorBenefits.map((benefit, idx) => {
              const IconComponent = iconMap[benefit.icon] || CheckCircle;
              return (
                <li key={benefit.titleKey} className="flex items-start gap-4">
                  <CheckCircle size={24} className="flex-shrink-0 mt-1" style={{ color: primaryColor }} />
                  <div>
                    <h4 className="font-bold text-lg mb-1">
                      {t(benefit.titleKey)}
                    </h4>
                    <p className="text-gray-600">
                      {t(benefit.descKey)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="lg:w-1/2">
          <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-lg">
            <h3 className="text-2xl font-bold mb-6">{t('form.title')}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Interest Selection */}
              <div>
                <Label className="text-base font-semibold mb-3 block">{t('form.interests')} *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {interestOptions.map((option) => {
                    const IconComp = option.icon;
                    const isSelected = formData.interests.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleInterestToggle(option.id)}
                        className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-current bg-opacity-10' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        style={isSelected ? { 
                          borderColor: primaryColor, 
                          backgroundColor: `${primaryColor}10` 
                        } : {}}
                      >
                        {isSelected && (
                          <div 
                            className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: primaryColor }}
                          >
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                        <IconComp 
                          size={28} 
                          style={{ color: isSelected ? primaryColor : '#6B7280' }}
                        />
                        <span 
                          className={`text-sm font-medium text-center ${isSelected ? '' : 'text-gray-600'}`}
                          style={isSelected ? { color: primaryColor } : {}}
                        >
                          {t(option.labelKey)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">{t('form.fullName')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="company">{t('form.company')} *</Label>
                  <Input
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="country">{t('form.country')} *</Label>
                  <Input
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t('form.email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="whatsapp">{t('form.whatsapp')}</Label>
                  <Input
                    id="whatsapp"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="category">{t('form.category')} *</Label>
                  <Select
                    required
                    value={formData.category}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder={t('form.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryKeys.map((catKey) => (
                        <SelectItem key={catKey} value={catKey}>
                          {t(`categories.${catKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message">{t('form.message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                className="w-full text-white py-6"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = settings.colors?.primaryHover || '#0A5F4A'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('form.submitting') : t('form.submit')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
